import { Check } from "lucide-react";
import { cn } from "@/utils";
import type { StepDef } from "./types";

interface ApplicationStepperProps {
  steps: StepDef[];
  current: number;
  /** Returns true when a step's requirements are satisfied. */
  isComplete: (id: number) => boolean;
  onJump: (id: number) => void;
}

/**
 * Vertical timeline navigator — the signature of the application "cockpit".
 * A continuous spine connects numbered nodes; finished steps fill teal with a
 * check, the active step glows electric, upcoming steps stay dim. Every node
 * is a jump target.
 */
export function ApplicationStepper({
  steps,
  current,
  isComplete,
  onJump,
}: ApplicationStepperProps) {
  return (
    <ol className="space-y-0.5">
      {steps.map((step, i) => {
        const active = current === step.id;
        const done = isComplete(step.id) && !active;
        const isLast = i === steps.length - 1;

        return (
          <li key={step.id}>
            <button
              type="button"
              onClick={() => onJump(step.id)}
              aria-current={active ? "step" : undefined}
              className="group flex w-full items-stretch gap-3.5 rounded-xl p-1.5 text-left transition-colors hover:bg-[color:var(--landing-surface-hover)]"
            >
              {/* Node + spine */}
              <div className="flex flex-col items-center">
                <span
                  className={cn(
                    "relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[13px] font-bold transition-all duration-300",
                    active && "scale-105",
                  )}
                  style={
                    active
                      ? {
                          background:
                            "var(--acc2)",
                          color: "#08121A",
                          boxShadow: "0 0 18px rgba(var(--acc2-rgb),0.45)",
                        }
                      : done
                        ? {
                            background: "rgba(var(--pos-rgb),0.14)",
                            color: "var(--pos)",
                            border: "1px solid rgba(var(--pos-rgb),0.4)",
                          }
                        : {
                            background: "var(--landing-surface)",
                            color: "var(--landing-text-muted)",
                            border: "1px solid var(--landing-glass-border)",
                          }
                  }
                >
                  {done ? <Check className="h-4 w-4" /> : step.id}
                </span>
                {!isLast && (
                  <span
                    className="my-1 w-0.5 flex-1 rounded-full"
                    style={{
                      minHeight: 18,
                      background: isComplete(step.id)
                        ? "linear-gradient(180deg, rgba(var(--pos-rgb),0.6), rgba(var(--acc2-rgb),0.25))"
                        : "var(--landing-glass-border)",
                    }}
                  />
                )}
              </div>

              {/* Label */}
              <div className={cn("pb-3 pt-1", isLast && "pb-1")}>
                <p
                  className="text-[10px] font-bold uppercase tracking-[0.18em]"
                  style={{
                    color: active
                      ? "#00B0D8"
                      : "var(--landing-text-dim)",
                  }}
                >
                  Step {step.id}
                </p>
                <p
                  className="text-sm font-semibold leading-tight transition-colors"
                  style={{
                    color: active
                      ? "var(--landing-text)"
                      : done
                        ? "var(--landing-text-secondary)"
                        : "var(--landing-text-muted)",
                  }}
                >
                  {step.title}
                </p>
                <p
                  className={cn(
                    "mt-0.5 text-xs leading-snug transition-all",
                    active ? "opacity-100" : "opacity-0 max-h-0 hidden md:block md:opacity-60 md:max-h-10",
                  )}
                  style={{ color: "var(--landing-text-muted)" }}
                >
                  {step.description}
                </p>
              </div>
            </button>
          </li>
        );
      })}
    </ol>
  );
}
